// PWA service-worker registration + install-prompt capture + update detection.
// Call once at app bootstrap. Browsers without SW support silently no-op.

let deferredInstallPrompt = null;
const installListeners = new Set();
const updateListeners = new Set();

function emitInstallChange() {
  installListeners.forEach((fn) => {
    try { fn(deferredInstallPrompt); } catch { /* ignore */ }
  });
}

function emitUpdateReady(registration) {
  updateListeners.forEach((fn) => {
    try { fn(registration); } catch { /* ignore */ }
  });
}

export function registerPWA() {
  if (typeof window === 'undefined') return;

  // Capture install prompt — fires once per page lifecycle when the browser
  // decides the site meets install criteria.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    emitInstallChange();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    emitInstallChange();
  });

  if (!('serviceWorker' in navigator)) return;

  // Register the shared service worker (push + offline cache share one file).
  window.addEventListener('load', () => {
    // Snapshot whether a controller already exists BEFORE registration finishes —
    // used to avoid the first-install controllerchange triggering an unwanted reload.
    const hadControllerAtStart = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.register('/service-worker.js').then((registration) => {
      // If a worker is already waiting (previous visit left it stuck), surface the update now.
      if (registration.waiting && navigator.serviceWorker.controller) {
        emitUpdateReady(registration);
      }
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            emitUpdateReady(registration);
          }
        });
      });
    }).catch((err) => {
      console.warn('[PWA] Service worker registration failed:', err);
    });

    // Reload the page only when a NEW worker takes over an existing session —
    // not on the first-ever install (no previous controller) which would cause a stray reload.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing || !hadControllerAtStart) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

export function onInstallAvailabilityChange(listener) {
  installListeners.add(listener);
  listener(deferredInstallPrompt);
  return () => installListeners.delete(listener);
}

export function onUpdateReady(listener) {
  updateListeners.add(listener);
  return () => updateListeners.delete(listener);
}

export async function triggerInstallPrompt() {
  if (!deferredInstallPrompt) return 'unavailable';
  deferredInstallPrompt.prompt();
  const choice = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  emitInstallChange();
  return choice.outcome; // 'accepted' | 'dismissed'
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  const matches = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari uses navigator.standalone
  // eslint-disable-next-line no-restricted-globals
  const iOSStandalone = !!window.navigator && !!window.navigator.standalone;
  return !!(matches || iOSStandalone);
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function applyUpdate(registration) {
  if (registration && registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    // Belt-and-braces: if controllerchange has already fired earlier in this
    // session, the auto-reload path is a no-op. Force a reload after a short
    // delay so the user always lands on the fresh build.
    setTimeout(() => {
      try { window.location.reload(); } catch { /* ignore */ }
    }, 500);
  }
}
